/**
 * Contextual Help Drawer Component
 * Provides context-aware help content in a slide-out drawer
 */

import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  BookOpen,
  HelpCircle,
  Lightbulb,
  Search,
  ExternalLink,
  CheckCircle2,
  Info,
  Zap,
} from "lucide-react";
import { HelpContent } from "@/types/help-content";
import { cn } from "@/lib/utils";

interface ContextualHelpDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  helpContent: HelpContent | null;
  onNavigateToPage?: (pageId: string) => void;
}

export function ContextualHelpDrawer({
  open,
  onOpenChange,
  helpContent,
  onNavigateToPage,
}: ContextualHelpDrawerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("functionality");

  if (!helpContent) return null;

  // Filter FAQ based on search
  const filteredFAQ = helpContent.faq.filter(
    (item) =>
      searchQuery === "" ||
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter user guide tasks based on search
  const filteredTasks = helpContent.userGuide.commonTasks.filter(
    (task) =>
      searchQuery === "" ||
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[600px] p-0 flex flex-col"
      >
        {/* Header */}
        <SheetHeader className="bg-muted p-6 space-y-3 border-b">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <HelpCircle className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <SheetTitle className="text-2xl font-semibold">
                {helpContent.title}
              </SheetTitle>
              <SheetDescription className="mt-2 text-base">
                {helpContent.description}
              </SheetDescription>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search help content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </SheetHeader>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="border-b bg-background px-6 pt-4">
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="functionality" className="gap-2">
                <Info className="h-4 w-4" />
                <span className="hidden sm:inline">Functionality</span>
                <span className="sm:hidden">Info</span>
              </TabsTrigger>
              <TabsTrigger value="guide" className="gap-2">
                <BookOpen className="h-4 w-4" />
                <span className="hidden sm:inline">User Guide</span>
                <span className="sm:hidden">Guide</span>
              </TabsTrigger>
              <TabsTrigger value="faq" className="gap-2">
                <HelpCircle className="h-4 w-4" />
                FAQ
                {searchQuery && filteredFAQ.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                    {filteredFAQ.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Functionality Tab */}
          <TabsContent
            value="functionality"
            className="flex-1 overflow-hidden m-0"
          >
            <ScrollArea className="h-full">
              <div className="p-6 space-y-6">
                {/* Overview */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Info className="h-5 w-5 text-primary" />
                    Overview
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {helpContent.functionality.overview}
                  </p>
                </div>

                {/* Key Features */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-primary" />
                    Key Features
                  </h3>
                  <ul className="space-y-2">
                    {helpContent.functionality.keyFeatures.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-sm text-muted-foreground"
                      >
                        <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Use Cases */}
                {helpContent.functionality.useCases && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-primary" />
                      Common Use Cases
                    </h3>
                    <ul className="space-y-2">
                      {helpContent.functionality.useCases.map((useCase, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-2 text-sm text-muted-foreground"
                        >
                          <span className="text-primary font-medium mt-0.5">•</span>
                          <span>{useCase}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* User Guide Tab */}
          <TabsContent value="guide" className="flex-1 overflow-hidden m-0">
            <ScrollArea className="h-full">
              <div className="p-6 space-y-6">
                {/* Getting Started */}
                {helpContent.userGuide.gettingStarted && (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2 text-primary">
                      <Zap className="h-4 w-4" />
                      Getting Started
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {helpContent.userGuide.gettingStarted}
                    </p>
                  </div>
                )}

                {/* Common Tasks */}
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-primary" />
                    Step-by-Step Guide
                  </h3>

                  {filteredTasks.length === 0 && searchQuery ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No tasks found matching "{searchQuery}"</p>
                    </div>
                  ) : (
                    <Accordion type="single" collapsible className="space-y-2">
                      {filteredTasks.map((task, index) => (
                        <AccordionItem
                          key={index}
                          value={`task-${index}`}
                          className="border rounded-lg"
                        >
                          <AccordionTrigger className="px-4 hover:no-underline">
                            <div className="flex items-center gap-3 text-left">
                              <Badge
                                variant="outline"
                                className="h-6 w-6 rounded-full p-0 flex items-center justify-center flex-shrink-0"
                              >
                                {task.step}
                              </Badge>
                              <span className="font-medium">{task.title}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <p className="text-sm text-muted-foreground mb-3">
                              {task.description}
                            </p>
                            {task.tips && task.tips.length > 0 && (
                              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                                <p className="text-xs font-semibold text-primary flex items-center gap-1">
                                  <Lightbulb className="h-3 w-3" />
                                  Tips
                                </p>
                                <ul className="space-y-1">
                                  {task.tips.map((tip, tipIndex) => (
                                    <li
                                      key={tipIndex}
                                      className="text-xs text-muted-foreground flex items-start gap-2"
                                    >
                                      <span className="text-primary mt-0.5">•</span>
                                      <span>{tip}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  )}
                </div>

                {/* General Tips */}
                {helpContent.userGuide.tips && helpContent.userGuide.tips.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-amber-900 dark:text-amber-100">
                      <Lightbulb className="h-4 w-4" />
                      Pro Tips
                    </h3>
                    <ul className="space-y-2">
                      {helpContent.userGuide.tips.map((tip, index) => (
                        <li
                          key={index}
                          className="text-sm text-amber-900 dark:text-amber-100 flex items-start gap-2"
                        >
                          <span className="text-amber-600 dark:text-amber-400 mt-0.5">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="flex-1 overflow-hidden m-0">
            <ScrollArea className="h-full">
              <div className="p-6 space-y-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-primary" />
                  Frequently Asked Questions
                </h3>

                {filteredFAQ.length === 0 && searchQuery ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No FAQs found matching "{searchQuery}"</p>
                  </div>
                ) : (
                  <Accordion type="single" collapsible className="space-y-2">
                    {filteredFAQ.map((faq, index) => (
                      <AccordionItem
                        key={index}
                        value={`faq-${index}`}
                        className="border rounded-lg"
                      >
                        <AccordionTrigger className="px-4 hover:no-underline text-left">
                          <span className="font-medium pr-4">{faq.question}</span>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {faq.answer}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Footer with Related Pages */}
        {helpContent.relatedPages && helpContent.relatedPages.length > 0 && (
          <div className="border-t bg-muted p-4">
            <p className="text-xs font-semibold text-muted-foreground mb-2">
              Related Pages
            </p>
            <div className="flex flex-wrap gap-2">
              {helpContent.relatedPages.map((page, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (onNavigateToPage) {
                      onNavigateToPage(page.pageId);
                      onOpenChange(false);
                    }
                  }}
                  className="h-8 text-xs"
                >
                  {page.label}
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Button>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
